// Utilidades para validación de datos

// Validar email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validar teléfono (formato mexicano)
export const isValidPhone = (phone) => {
  // Acepta formatos: 1234567890, (123) 456-7890, 123-456-7890, +52 123 456 7890
  const phoneRegex = /^(\+52\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

// Validar código de barras
export const isValidBarcode = (barcode) => {
  if (!barcode) return false;

  const cleaned = barcode.toString().replace(/[^0-9]/g, '');

  // Longitudes válidas para códigos de barras comunes
  const validLengths = [8, 12, 13, 14];
  return validLengths.includes(cleaned.length);
};

// Validar precio
export const isValidPrice = (price) => {
  if (typeof price !== 'number') {
    price = parseFloat(price);
  }
  return !isNaN(price) && price > 0 && price < 999999;
};

// Validar stock/cantidad
export const isValidQuantity = (quantity) => {
  if (typeof quantity !== 'number') {
    quantity = parseInt(quantity);
  }
  return !isNaN(quantity) && quantity >= 0 && quantity <= 999999;
};

// Validar nombre de producto
export const isValidProductName = (name) => {
  return typeof name === 'string' &&
         name.trim().length >= 2 &&
         name.trim().length <= 100;
};

// Validar RFC (México)
export const isValidRFC = (rfc) => {
  if (!rfc) return false;

  // RFC Persona Física: 4 letras + 6 dígitos + 3 caracteres
  const rfcPersonaFisica = /^[A-ZÑ&]{4}[0-9]{6}[A-V1-9][A-Z1-9][0-9A]$/;

  // RFC Persona Moral: 3 letras + 6 dígitos + 3 caracteres
  const rfcPersonaMoral = /^[A-ZÑ&]{3}[0-9]{6}[A-V1-9][A-Z1-9][0-9A]$/;

  const rfcUpper = rfc.toUpperCase().replace(/\s/g, '');

  return rfcPersonaFisica.test(rfcUpper) || rfcPersonaMoral.test(rfcUpper);
};

// Validar CURP (México)
export const isValidCURP = (curp) => {
  if (!curp) return false;

  const curpRegex = /^[A-Z]{1}[AEIOU]{1}[A-Z]{2}[0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])[HM]{1}(AS|BC|BS|CC|CS|CH|CL|CM|DF|DG|GT|GR|HG|JC|MC|MN|MS|NT|NL|OC|PL|QT|QR|SP|SL|SR|TC|TS|TL|VZ|YN|ZS|NE)[B-DF-HJ-NP-TV-Z]{3}[0-9A-Z]{1}$/;

  return curpRegex.test(curp.toUpperCase());
};

// Validar contraseña
export const isValidPassword = (password) => {
  if (!password || password.length < 6) return false;

  // Al menos una letra y un número
  const hasLetter = /[a-zA-Z]/.test(password);
  const hasNumber = /[0-9]/.test(password);

  return hasLetter && hasNumber;
};

// Validar porcentaje
export const isValidPercentage = (percentage) => {
  if (typeof percentage !== 'number') {
    percentage = parseFloat(percentage);
  }
  return !isNaN(percentage) && percentage >= 0 && percentage <= 100;
};

// Validar descuento
export const isValidDiscount = (discount, maxPrice = null) => {
  if (typeof discount !== 'number') {
    discount = parseFloat(discount);
  }

  if (isNaN(discount) || discount < 0) return false;

  // Si se proporciona un precio máximo, validar que el descuento no sea mayor
  if (maxPrice && discount > maxPrice) return false;

  return true;
};

// Validar fecha
export const isValidDate = (date) => {
  const dateObj = new Date(date);
  return dateObj instanceof Date && !isNaN(dateObj);
};

// Validar que una fecha no sea futura
export const isNotFutureDate = (date) => {
  if (!isValidDate(date)) return false;
  return new Date(date) <= new Date();
};

// Validar rango de fechas
export const isValidDateRange = (startDate, endDate) => {
  if (!isValidDate(startDate) || !isValidDate(endDate)) return false;
  return new Date(startDate) <= new Date(endDate);
};

// Validar objeto no vacío
export const isNotEmpty = (obj) => {
  if (obj === null || obj === undefined) return false;
  if (typeof obj === 'string') return obj.trim().length > 0;
  if (Array.isArray(obj)) return obj.length > 0;
  if (typeof obj === 'object') return Object.keys(obj).length > 0;
  return true;
};

// Validar número de tarjeta de crédito (algoritmo de Luhn)
export const isValidCreditCard = (cardNumber) => {
  if (!cardNumber) return false;

  const cleaned = cardNumber.replace(/\s/g, '');

  if (!/^\d+$/.test(cleaned)) return false;
  if (cleaned.length < 13 || cleaned.length > 19) return false;

  // Algoritmo de Luhn
  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned[i]);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

// Validar formato de dirección IP
export const isValidIP = (ip) => {
  const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
  return ipRegex.test(ip);
};

// Validar puerto de red
export const isValidPort = (port) => {
  const portNum = parseInt(port);
  return !isNaN(portNum) && portNum >= 1 && portNum <= 65535;
};